'use client';

import type {ArtaQuoteType} from '@/hooks/use-managed-shipping/typings';
import type {CurrencyType} from '@/redux/reducers/typings';
import type {ShippingQuoteResultProps as Props} from './typings';

import Icon from '@/atoms/Icon/Icon';
import {currencyCodeToSymbol} from '@/utils/helpers/price';
import AddressSummary from './AddressSummary';
import {
  StyledBackButton,
  StyledPriceTag,
  StyledQuoteAlert,
  StyledQuoteAlertMessagesList,
  StyledShippingQuoteResult,
} from './styles';
import {getEstimatePriceContent} from './utils';

const mapTypeToLabel: Record<ArtaQuoteType, string> = {
  select: 'Select',
  self_ship: 'Self Ship',
  parcel: 'Parcel',
  premium: 'Premium',
};

/**
 * @description Show disqualification messages if any.
 * @param {object} props
 * @param {Array|undefined} props.disqualifications - (optional) Array of disqualification objects.
 * @returns {JSX.Element|null} List of disqualification messages or `null` if none are available.
 */
export const DisqualificationMessages = ({
  disqualifications,
}: Pick<NonNullable<Props['estimate']>, 'disqualifications'>): JSX.Element | null => {
  if (!disqualifications || disqualifications.length === 0) {
    return null;
  }

  return (
    <StyledQuoteAlertMessagesList aria-label="Quote Disqualification Messages">
      {disqualifications.map(({type, message}) => (
        <li key={type} aria-label={mapTypeToLabel[type]}>
          <span>{mapTypeToLabel[type]}: </span>
          <span>{message}</span>
        </li>
      ))}
    </StyledQuoteAlertMessagesList>
  );
};

/**
 * @description Show successful shipping quote result.
 * - If 'quoted', show starting price.
 * - If 'disqualified', show disqualification messages.
 * @param {object} props
 * @param {Object} props.estimate - The shipping estimate object.
 * @returns {JSX.Element} The rendered success result markup.
 */
export const SuccessResult = ({
  estimate,
}: {
  estimate: NonNullable<Props['estimate']>;
}): JSX.Element => {
  const content = getEstimatePriceContent(estimate);
  return (
    <>
      <div>
        <AddressSummary
          location={estimate.from}
          variant="origin"
          aria-label="Origin Address Summary"
        />
        <AddressSummary
          location={estimate.to}
          variant="destination"
          aria-label="Destination Address Summary"
        />
      </div>

      {content.status === 'quoted' ? (
        <StyledQuoteAlert $variant="quoted">
          <p>Shipping Starts at</p>
          <StyledPriceTag>
            <b>
              {currencyCodeToSymbol(estimate.currency as CurrencyType)}
              {estimate.shipping_price.min}
            </b>
            {estimate.currency}
          </StyledPriceTag>
        </StyledQuoteAlert>
      ) : (
        <StyledQuoteAlert $variant="disqualified">
          <p>Shipping Disqualified: </p>
          <DisqualificationMessages disqualifications={estimate.disqualifications} />
        </StyledQuoteAlert>
      )}
    </>
  );
};

/**
 * @description Show error message if shipping quote fetch failed.
 * @param {object} props
 * @param {string|null} props.error - The error message if available.
 * @param {Object} props.origin - The origin address object.
 * @returns {JSX.Element} The rendered error result markup.
 */
export const ErrorResult = ({error, origin}: Pick<Props, 'error' | 'origin'>): JSX.Element => (
  <>
    <div>
      <AddressSummary location={origin} variant="origin" aria-label="Origin Address Summary" />
    </div>
    <StyledQuoteAlert $variant="error">
      <p>{error || 'Failed to get shipping quote'}</p>
    </StyledQuoteAlert>
  </>
);

/**
 * @description Show shipping quote result: success or error.
 * @param {Props} props - component props.
 * @param {Object|null} props.estimate - The shipping estimate object if available.
 * @param {Object} props.origin - The origin address object.
 * @param {string|null} props.error - The error message if available.
 * @param {Function} props.handleStepBack - Function to go back to the previous step.
 * @returns {JSX.Element} The rendered shipping quote result markup.
 */
const ShippingQuoteResult = ({estimate, origin, error, handleStepBack}: Props): JSX.Element => {
  return (
    <StyledShippingQuoteResult>
      {estimate ? (
        <SuccessResult estimate={estimate} />
      ) : (
        <ErrorResult error={error} origin={origin} />
      )}
      <div>
        <StyledBackButton type="button" onClick={handleStepBack}>
          <Icon type={'chevron-left'} fontSize={10} /> <span>Change Destination</span>
        </StyledBackButton>
      </div>
    </StyledShippingQuoteResult>
  );
};

export default ShippingQuoteResult;
